import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useFindings, type Severity } from '@/lib/findings'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/SeverityBadge'
import { relative } from '@/lib/format'

export function ErrorsPage() {
  const [activeOnly, setActiveOnly] = useState(true)
  const [severity, setSeverity] = useState<Severity | null>(null)
  const { data, isLoading } = useFindings({
    activeOnly,
    severity: severity ?? null,
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Errors</h1>
          <p className="text-muted-foreground">
            Log findings grouped by fingerprint. Each row = one unique error shape.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={activeOnly ? 'default' : 'ghost'}
            onClick={() => setActiveOnly(!activeOnly)}
          >
            {activeOnly ? 'Active only' : 'All'}
          </Button>
          {(['critical', 'error', 'warning', 'info'] as Severity[]).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={severity === s ? 'default' : 'ghost'}
              onClick={() => setSeverity(severity === s ? null : s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <Skeleton className="h-40" />}
      {!isLoading && data?.length === 0 && (
        <Card><CardContent className="pt-6 text-muted-foreground">No findings.</CardContent></Card>
      )}
      <div className="space-y-2">
        {data?.map((f) => (
          <Card key={f.id}>
            <CardHeader className="flex-row items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                <SeverityBadge severity={f.severity} />
                <CardTitle className="text-sm truncate">
                  <Link to={`/errors/${f.id}`} className="hover:underline">
                    {f.signature_short}
                  </Link>
                </CardTitle>
              </div>
              <div className="text-xs text-muted-foreground flex gap-3">
                <span>{f.pattern_matched}</span>
                <span>×{f.count_total}</span>
                <span>{relative(f.last_seen)}</span>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  )
}
